import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const FOUNDER_PHOTO = '/assets/founder.jpg';
const WHATSAPP = 'https://wa.me/918087645456?text=' + encodeURIComponent(
  'Hello Dr. Vikram, I would like to book an INTIVIV consultation.'
);

const credentials = [
  { icon: 'fa-user-doctor', text: 'Cosmetic Gynaecologist & Sexual Wellness Expert' },
  { icon: 'fa-stethoscope', text: 'Laparoscopic / Robotic / Reconstructive Pelvic Surgeon' },
  { icon: 'fa-award',       text: '15+ years of clinical experience' },
  { icon: 'fa-hospital',    text: "Founder · ANAMIVA Clinic at Dr. Dukle's Hospital & Research Centre, Goa" },
  { icon: 'fa-microscope',  text: 'Pioneer of Gynaecologist-Led Intimate Wellness in India' },
  { icon: 'fa-rocket',      text: "Founder · Gynovate AI™ — India's first Co-pilot for Gynaecologists" },
];

export default function Founder() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', paddingBottom: 'var(--bottom-h)' }}>
      {/* Header */}
      <div style={{ padding: '20px 16px 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={() => navigate(-1)}
          className="btn-ghost"
          style={{ padding: '6px 8px' }}
          data-testid="founder-back"
        >
          <i className="fa-solid fa-arrow-left" />
        </button>
        <div className="eyebrow" style={{ marginBottom: 0 }}>The Founder</div>
      </div>

      {/* Hero photo + name */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ padding: '0 16px' }}
      >
        <div style={{
          position: 'relative', width: '100%', aspectRatio: '4 / 5',
          background: 'linear-gradient(180deg, var(--bg2), #060606)',
          border: '1px solid var(--border)', overflow: 'hidden',
          marginBottom: 16,
        }}>
          <img
            src={FOUNDER_PHOTO}
            alt="Dr. Vikram Dukle — CEO & Founder, INTIVIV"
            data-testid="founder-photo"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, transparent 55%, rgba(10,10,10,0.95) 100%)',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', left: 16, right: 16, bottom: 14,
            color: 'var(--text)',
          }}>
            <div style={{ fontSize: '0.71rem', letterSpacing: '0.25em', color: 'var(--gold)', fontWeight: 700, marginBottom: 4 }}>
              CEO & FOUNDER
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.05rem', lineHeight: 1.1 }}>
              Dr. Vikram <span className="gold-italic">Dukle</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Pull quote */}
      <div style={{ padding: '0 20px 18px' }}>
        <p style={{
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          fontSize: '1.16rem',
          lineHeight: 1.55,
          color: 'var(--text)',
          borderLeft: '2px solid var(--gold)',
          paddingLeft: 14,
        }}>
          "Intimate wellness is not a luxury — it's a fundamental dimension of health.
          INTIVIV exists so every woman, and every man, has a private, expert, judgement-free
          space to thrive."
        </p>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: 10, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
          — Dr. Vikram Dukle, Founder
        </div>
      </div>

      {/* Description */}
      <div style={{ padding: '0 20px 20px' }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>About</div>
        <p style={{ fontSize: '0.94rem', lineHeight: 1.75, color: 'var(--text-muted)', marginBottom: 12 }}>
          Dr. Vikram Dukle is a cosmetic gynaecologist, sexual wellness expert, and
          laparoscopic / robotic / reconstructive pelvic surgeon with over 15 years of
          clinical experience. He founded INTIVIV™ to bring gynaecologist-grade intimate
          care out of the clinic and into the lives of women and couples who deserve it
          everywhere.
        </p>
        <p style={{ fontSize: '0.94rem', lineHeight: 1.75, color: 'var(--text-muted)', marginBottom: 12 }}>
          From his ANAMIVA Clinic at Dr. Dukle's Hospital &amp; Research Centre in Goa,
          Dr. Dukle has helped thousands navigate dryness, painful intercourse, postpartum
          recovery, perimenopause, libido changes, pelvic floor concerns and the deeper
          emotional layers of intimacy. INTIVIV is the digital extension of that care —
          combining his expertise with a private AI coach (VIVI), a clinically-grounded
          education library, and personal assessments built to guide your unique wellness
          path.
        </p>
        <p style={{ fontSize: '0.94rem', lineHeight: 1.75, color: 'var(--text-muted)' }}>
          Every article, every assessment, and every product recommendation in INTIVIV™
          carries Dr. Dukle's clinical signature — because intimate health deserves nothing
          less.
        </p>
      </div>

      {/* Credentials */}
      <div style={{ padding: '0 16px 24px' }}>
        <div className="eyebrow" style={{ padding: '0 4px', marginBottom: 12 }}>Credentials</div>
        {credentials.map(c => (
          <div key={c.text} style={{
            display: 'flex', alignItems: 'flex-start', gap: 12,
            padding: '12px 14px', background: 'var(--bg2)',
            border: '1px solid var(--border-soft)', marginBottom: 8,
          }}>
            <i className={`fa-solid ${c.icon}`} style={{ color: 'var(--gold)', fontSize: '1.03rem', marginTop: 3 }} />
            <span style={{ fontSize: '0.9rem', color: 'var(--text)', lineHeight: 1.55 }}>{c.text}</span>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div style={{ padding: '0 16px 24px' }}>
        <a
          href={WHATSAPP}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold w-full"
          style={{ textDecoration: 'none', fontSize: '0.83rem' }}
          data-testid="founder-whatsapp"
        >
          <i className="fa-brands fa-whatsapp" /> BOOK CONSULT ON WHATSAPP
        </a>
        <div style={{ textAlign: 'center', marginTop: 10, fontSize: '0.77rem', color: 'var(--text-dim)' }}>
          Direct line to Dr. Vikram's team · +91&nbsp;80876&nbsp;45456
        </div>
      </div>
    </div>
  );
}
